// De inzet bij een huishouden in uren en euro's, voor de SiSa/SPUK-verantwoording
// (indicatoren 3, 4, 6 en 7 in docs/features/huishouden-vaste-gegevens.md).
//
// Bron: de afspraken zelf (besluit 8). Een voorbije afspraak telt met zijn duur
// × het aantal medewerkers (besluit B), in het jaar waarin hij begint, als
// uitvoering of advies volgens de soort ('Telt als' bij 'Soorten afspraak').
// Er is bewust geen correctieveld (besluit C): wie uren mist, zet een afspraak
// achteraf in de agenda.
//
// Nodig naast `tasksHours.js` omdat die per dag optelt zonder soort en zonder
// medewerkers; die twee bepalen hier juist het bedrag.
//
// 'TARIEF NIET INGESTELD' IS NIET €0: zonder tarief is `cost` null, ook als
// er uren zijn. Een afnemer toont dan dat het tarief ontbreekt.
// AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
// AI-CONTEXT: See docs/pitfalls.md#database-subkind-hidden-behind-a-link-field

import isAppointment from './taskKind'
import { hoursBetween, toDayKeyInTimeZone } from '../dateHelpers/adressDashboard/tasksHours'
import { kindKeyForTitle } from '../tasks/resolveAppointmentKinds'

/**
 * @typedef {object} EffortBucket
 * @property {number} hours - Uren × medewerkers
 * @property {number|null} cost - Uren × tarief; null als het tarief niet is ingesteld
 * @property {number} appointments - Aantal afspraken dat meetelt
 */

/**
 * @typedef {object} Effort
 * @property {EffortBucket} uitvoering
 * @property {EffortBucket} advies
 */

/**
 * @typedef {object} HouseholdEffort
 * @property {Effort} year - Afspraken die in jaar T begonnen
 * @property {Effort} cumulative - Afspraken die in jaar T of eerder begonnen
 */

/** @param {*} ts @returns {Date|null} */
const asDate = (ts) => {
    if (!ts) return null
    const d = ts.toDate ? ts.toDate() : new Date(ts)
    return Number.isFinite(d.getTime()) ? d : null
}

/**
 * Waarvoor deze afspraak telt. Een afspraak zonder `Kind` (van vóór de soorten,
 * of 'Anders…') valt terug op zijn titel, zoals de keuzelijst dat ook doet.
 * @param {object} task
 * @param {Array<{key: string, label: string, inzet: string}>} kinds
 * @returns {'uitvoering'|'advies'|''}
 */
const inzetVan = (task, kinds) => {
    const key = task.Kind || kindKeyForTitle(kinds, task.Title)
    const kind = kinds.find((k) => k.key === key)
    return kind ? kind.inzet : ''
}

/** @param {object} task @returns {number} Minstens 1: een afspraak is door iemand gedaan */
const memberCount = (task) => Math.max(1, new Set(Array.isArray(task.TeamMembers) ? task.TeamMembers : []).size)

/** @returns {{uitvoering: {hours: number, appointments: number}, advies: {hours: number, appointments: number}}} */
const empty = () => ({ uitvoering: { hours: 0, appointments: 0 }, advies: { hours: 0, appointments: 0 } })

/**
 * @param {{hours: number, appointments: number}} bucket
 * @param {number|null} rate
 * @returns {EffortBucket}
 */
const priced = (bucket, rate) => ({ ...bucket, cost: rate === null ? null : bucket.hours * rate })

/**
 * @param {Array<object>} tasks - Taken van één huishouden (of meer; de som telt)
 * @param {Array<{key: string, label: string, inzet: string}>} kinds - Uit `resolveAppointmentKinds`
 * @param {{uitvoering: number|null, advies: number|null}} rates - Uit `readHourlyRates`
 * @param {number} year - Verantwoordingsjaar T
 * @param {Date} [now] - Wat 'voorbij' is
 * @returns {HouseholdEffort}
 */
const householdEffort = (tasks, kinds, rates, year, now = new Date()) => {
    const jaar = empty()
    const tot = empty()

    ;(tasks || []).forEach((task) => {
        if (!task || task.Deleted === true || !isAppointment(task)) return
        const inzet = inzetVan(task, kinds || [])
        if (!inzet) return
        const end = asDate(task.TimestampEnd)
        if (!end || end > now) return
        const hours = hoursBetween(task.Timestamp, task.TimestampEnd) * memberCount(task)
        if (!hours) return

        // Het jaar van de start, in Nederlandse tijd: een afspraak die op
        // 31 december om 23:00 begint hoort bij dat jaar.
        const startYear = Number(String(toDayKeyInTimeZone(task.Timestamp)).slice(0, 4))
        if (startYear > year) return
        tot[inzet].hours += hours
        tot[inzet].appointments += 1
        if (startYear === year) {
            jaar[inzet].hours += hours
            jaar[inzet].appointments += 1
        }
    })

    const prijs = (effort) => ({
        uitvoering: priced(effort.uitvoering, rates?.uitvoering ?? null),
        advies: priced(effort.advies, rates?.advies ?? null),
    })
    return { year: prijs(jaar), cumulative: prijs(tot) }
}

export default householdEffort
